import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";

import { ALL_PRODUCT_TYPES, MULTI_PRODUCT_LINE_STYLES } from "constants/chart.constants";
import {
  updateAnnotationsActive,
  updateChartProduct,
  updateMultiProductActive,
  updateMultiProductProperty
} from "store/features/charts/mpcActions";
import { MultiProductFeature } from "types/charts/feature";
import { getProductKey } from "utils/chart/getProductKey";

interface MultiProductState {
  otherProducts: string[];
  primaryProducts: string[];
  secondaryProducts: string[];
  productLineStyles: Record<string, string | number[]>;
  overrideLineStyles: Record<string, string | number[]>;
  overrideBinColors: boolean;
}

type ChartFeatures = {
  multiProduct?: Partial<MultiProductFeature>;
};

interface UseMultiProductActionsParams {
  id: string;
  product: string;
  features: ChartFeatures;
  canAddProduct: (prod: string, axis: string) => boolean;
}

/**
 * Returns the next available line style not already assigned to another product.
 */
export function getNextLineStyle(
  currentStyles: Record<string, string | number[]>
): string | number[] {
  const usedStyles = Object.values(currentStyles);
  for (const style of MULTI_PRODUCT_LINE_STYLES) {
    const isUsed = usedStyles.some((s) => JSON.stringify(s) === JSON.stringify(style));
    if (!isUsed) return style;
  }
  return MULTI_PRODUCT_LINE_STYLES[usedStyles.length % MULTI_PRODUCT_LINE_STYLES.length];
}

function getMultiProductState(features: ChartFeatures): MultiProductState {
  return {
    otherProducts: features?.multiProduct?.properties?.otherProducts || [],
    primaryProducts: features?.multiProduct?.properties?.primaryProducts || [],
    secondaryProducts: features?.multiProduct?.properties?.secondaryProducts || [],
    productLineStyles: features?.multiProduct?.properties?.productLineStyles || {},
    overrideLineStyles: features?.multiProduct?.properties?.overrideLineStyles || {},
    overrideBinColors: features?.multiProduct?.properties?.overrideBinColors ?? false
  };
}

/** Drops a product from a style map without mutating the original. */
function omitProduct(
  styles: Record<string, string | number[]>,
  productKey: string
): Record<string, string | number[]> {
  return Object.fromEntries(Object.entries(styles).filter(([key]) => key !== productKey));
}

export function useMultiProductActions({
  id,
  product,
  features,
  canAddProduct
}: UseMultiProductActionsParams) {
  const dispatch = useDispatch();
  const entryProductRef = useRef<string | null>(null);

  const dispatchMpcProperty = useCallback(
    (property: string, value: Primitive) => {
      dispatch(updateMultiProductProperty(id, property, value));
    },
    [id, dispatch]
  );

  const removeProductFromAll = useCallback(
    (productKey: string, state: MultiProductState) => {
      const remainingOtherProducts = state.otherProducts.filter((p) => p !== productKey);

      dispatchMpcProperty("otherProducts", remainingOtherProducts);
      dispatchMpcProperty(
        "primaryProducts",
        state.primaryProducts.filter((p) => p !== productKey)
      );
      dispatchMpcProperty(
        "secondaryProducts",
        state.secondaryProducts.filter((p) => p !== productKey)
      );
      dispatchMpcProperty(
        "productLineStyles",
        omitProduct(state.productLineStyles, productKey)
      );
      dispatchMpcProperty(
        "overrideLineStyles",
        omitProduct(state.overrideLineStyles, productKey)
      );

      // If the removed product is the base product, promote the first remaining product
      if (productKey === getProductKey(product) && remainingOtherProducts.length > 0) {
        const nextKey = remainingOtherProducts[0];
        const entry = Object.values(ALL_PRODUCT_TYPES).find((p) => p.key === nextKey);
        dispatch(updateChartProduct(id, entry?.label ?? nextKey));
      }
    },
    [product, id, dispatch, dispatchMpcProperty]
  );

  const addProductToAxis = useCallback(
    (
      productKey: string,
      axis: "primaryProducts" | "secondaryProducts",
      state: MultiProductState
    ) => {
      const oppositeAxis =
        axis === "primaryProducts" ? "secondaryProducts" : "primaryProducts";

      // Add to target axis
      dispatchMpcProperty(axis, [...state[axis], productKey]);

      // Remove from opposite axis if present
      if (state[oppositeAxis].includes(productKey)) {
        dispatchMpcProperty(
          oppositeAxis,
          state[oppositeAxis].filter((p) => p !== productKey)
        );
      }

      // Add to otherProducts and assign line style if not already present
      if (!state.otherProducts.includes(productKey)) {
        dispatchMpcProperty("otherProducts", [...state.otherProducts, productKey]);
        dispatchMpcProperty("productLineStyles", {
          ...state.productLineStyles,
          [productKey]: getNextLineStyle(state.productLineStyles)
        });
      }
    },
    [dispatchMpcProperty]
  );

  const removeProductFromAxis = useCallback(
    (
      productKey: string,
      axis: "primaryProducts" | "secondaryProducts",
      state: MultiProductState
    ) => {
      dispatchMpcProperty(
        axis,
        state[axis].filter((p) => p !== productKey)
      );
      dispatchMpcProperty(
        "otherProducts",
        state.otherProducts.filter((p) => p !== productKey)
      );
      dispatchMpcProperty(
        "productLineStyles",
        omitProduct(state.productLineStyles, productKey)
      );
      dispatchMpcProperty(
        "overrideLineStyles",
        omitProduct(state.overrideLineStyles, productKey)
      );
    },
    [dispatchMpcProperty]
  );

  const toggleMultiProduct = useCallback(
    (active: boolean, exitProduct?: string) => {
      dispatch(updateMultiProductActive(id, active));

      if (!active) {
        const state = getMultiProductState(features);
        const resolveProductLabel = (key: string): string => {
          const entry = Object.values(ALL_PRODUCT_TYPES).find((p) => p.key === key);
          return entry?.label ?? key;
        };
        const lastSelected =
          state.otherProducts.length === 1
            ? resolveProductLabel(state.otherProducts[0])
            : undefined;
        const restoreProduct =
          exitProduct ?? lastSelected ?? entryProductRef.current ?? product;

        dispatch(updateChartProduct(id, restoreProduct));
        entryProductRef.current = null;
        dispatchMpcProperty("otherProducts", []);
        dispatchMpcProperty("primaryProducts", []);
        dispatchMpcProperty("secondaryProducts", []);
        dispatchMpcProperty("productLineStyles", {});
        dispatchMpcProperty("overrideLineStyles", {});
      } else {
        entryProductRef.current = product;
        const productToAdd = getProductKey(product);
        dispatchMpcProperty("otherProducts", [productToAdd]);
        dispatchMpcProperty("primaryProducts", [productToAdd]);
        dispatchMpcProperty("productLineStyles", {
          [productToAdd]: MULTI_PRODUCT_LINE_STYLES[0]
        });
        dispatchMpcProperty("overrideLineStyles", {});
        dispatch(updateAnnotationsActive(id, false));
      }
    },
    [id, product, features, dispatch, dispatchMpcProperty]
  );

  const handleSetPrimary = useCallback(
    (prod: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const productKey = getProductKey(prod);
      const state = getMultiProductState(features);

      if (state.primaryProducts.includes(productKey)) {
        if (state.otherProducts.length === 1) {
          toggleMultiProduct(false, prod);
          return;
        }
        removeProductFromAxis(productKey, "primaryProducts", state);
        return;
      }

      if (!canAddProduct(prod, "primary")) return;
      addProductToAxis(productKey, "primaryProducts", state);
    },
    [features, canAddProduct, toggleMultiProduct, addProductToAxis, removeProductFromAxis]
  );

  const handleSetSecondary = useCallback(
    (prod: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const productKey = getProductKey(prod);
      const state = getMultiProductState(features);

      if (state.secondaryProducts.includes(productKey)) {
        if (state.otherProducts.length === 1) {
          toggleMultiProduct(false, prod);
          return;
        }
        removeProductFromAxis(productKey, "secondaryProducts", state);
        return;
      }

      if (!canAddProduct(prod, "secondary")) return;
      addProductToAxis(productKey, "secondaryProducts", state);
    },
    [features, canAddProduct, toggleMultiProduct, addProductToAxis, removeProductFromAxis]
  );

  const handleMultiProductClick = useCallback(
    (prod: string, isPrimaryDisabled: boolean, isSecondaryDisabled: boolean) => {
      const productKey = getProductKey(prod);
      const state = getMultiProductState(features);

      if (state.otherProducts.includes(productKey)) {
        if (state.otherProducts.length === 1) {
          toggleMultiProduct(false, prod);
          return;
        }
        removeProductFromAll(productKey, state);
      } else {
        const targetAxis = !isPrimaryDisabled
          ? "primaryProducts"
          : !isSecondaryDisabled
          ? "secondaryProducts"
          : null;
        if (!targetAxis) return;
        addProductToAxis(productKey, targetAxis, state);
      }
    },
    [features, toggleMultiProduct, removeProductFromAll, addProductToAxis]
  );

  const updateLineStyle = useCallback(
    (prod: string, style: string | number[]) => {
      const state = getMultiProductState(features);
      const key = getProductKey(prod);
      // Always record the pick against productLineStyles: it is what the product
      // draws with once the override is switched back off (EVA-56).
      dispatchMpcProperty("productLineStyles", {
        ...state.productLineStyles,
        [key]: style
      });
      // While the override is on, everything is forced solid — so the pick also
      // has to be flagged as an explicit exception to that.
      if (state.overrideBinColors) {
        dispatchMpcProperty("overrideLineStyles", {
          ...state.overrideLineStyles,
          [key]: style
        });
      }
    },
    [features, dispatchMpcProperty]
  );

  return {
    toggleMultiProduct,
    handleSetPrimary,
    handleSetSecondary,
    handleMultiProductClick,
    updateLineStyle,
    getNextLineStyle
  };
}
